import { ArrowRight, Check, Smartphone } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, saveRoomAccess } from "../api";
import { ErrorState, InlineNotice, LoadingState } from "../components/Feedback";

export function ConfirmationPage() {
  const { orderId = "" } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Record<string, string | number>>();
  const [hostName, setHostName] = useState("");
  const [deviceMode, setDeviceMode] = useState<"shared" | "multiple">("multiple");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const playToken = sessionStorage.getItem(`order:${orderId}:playToken`);

  useEffect(() => {
    api.order(orderId).then(({ order: value }) => setOrder(value)).catch((reason) => setError(reason.message));
  }, [orderId]);

  const createRoom = async (event: FormEvent) => {
    event.preventDefault();
    if (!playToken || !hostName.trim() || pending) return;
    setPending(true);
    setError("");
    try {
      const { access } = await api.createRoom(playToken, hostName, deviceMode);
      saveRoomAccess(access);
      sessionStorage.removeItem(`order:${orderId}:playToken`);
      navigate(`/sala/${access.roomId}`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "No pudimos crear la sala.");
    } finally {
      setPending(false);
    }
  };

  if (!order && !error) return <LoadingState label="Confirmando el acceso…" />;
  if (!order) return <ErrorState message={error} />;

  return (
    <div className="confirmation-page">
      <div className="confirmation-light" aria-hidden="true"><span /><span /><span /></div>
      <div className="confirmation-copy">
        <div className="success-seal"><Check aria-hidden="true" /></div>
        <h1>La experiencia ya es de ustedes.</h1>
        <p>Ahora creen la sala. Después podrán copiar el enlace o mostrar un código para que los demás entren.</p>
        {!playToken && (
          <InlineNotice tone="error">Este navegador ya no conserva el token de entrega. En la demo local, vuelve al catálogo y genera un acceso nuevo.</InlineNotice>
        )}
        <form onSubmit={createRoom}>
          <label htmlFor="host-name">¿Cómo te llamamos en la sala?</label>
          <input
            id="host-name"
            value={hostName}
            onChange={(event) => setHostName(event.target.value)}
            maxLength={40}
            autoComplete="nickname"
            placeholder="Tu nombre"
            required
          />
          <fieldset>
            <legend>¿Cómo van a jugar?</legend>
            <label className={deviceMode === "multiple" ? "device-choice is-selected" : "device-choice"}>
              <input type="radio" name="device-mode" value="multiple" checked={deviceMode === "multiple"} onChange={() => setDeviceMode("multiple")} />
              <Smartphone aria-hidden="true" /><span><strong>Un teléfono por persona</strong><small>Recomendado para pistas privadas.</small></span>
            </label>
            <label className={deviceMode === "shared" ? "device-choice is-selected" : "device-choice"}>
              <input type="radio" name="device-mode" value="shared" checked={deviceMode === "shared"} onChange={() => setDeviceMode("shared")} />
              <Smartphone aria-hidden="true" /><span><strong>Compartir este teléfono</strong><small>Las vistas privadas aparecen por turnos.</small></span>
            </label>
          </fieldset>
          {error && <InlineNotice tone="error">{error}</InlineNotice>}
          <button className="button button-citron button-large button-full" type="submit" disabled={!playToken || !hostName.trim() || pending}>
            {pending ? "Creando la sala…" : <>Crear nuestra sala <ArrowRight aria-hidden="true" /></>}
          </button>
        </form>
      </div>
    </div>
  );
}
